'use client';

import { createClient } from '@/lib/supabase/client';
import { CategoryName, GradeLevel, WorksheetStatus } from '@/lib/worksheetData';

export interface WorksheetRow {
  id: string;
  title: string;
  category: CategoryName;
  grade: GradeLevel;
  description: string;
  pages: number;
  file_size: string;
  file_path: string;
  file_name: string;
  status: WorksheetStatus;
  downloads: number;
  uploaded_at: string;
  created_at: string;
}

export interface WorksheetInsert {
  title: string;
  category: CategoryName;
  grade: GradeLevel;
  description: string;
  file: File;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns.some((p) => p.test(error.message));
  }
  return false;
}

export const worksheetService = {
  async getAll(filters?: {
    status?: WorksheetStatus | 'All';
    category?: CategoryName | 'All';
    grade?: GradeLevel | 'All';
    search?: string;
  }): Promise<WorksheetRow[]> {
    const supabase = createClient();
    try {
      let query = supabase.from('worksheets').select('*');

      if (filters?.status && filters.status !== 'All') {
        query = query.eq('status', filters.status);
      }
      if (filters?.category && filters.category !== 'All') {
        query = query.eq('category', filters.category);
      }
      if (filters?.grade && filters.grade !== 'All') {
        query = query.eq('grade', filters.grade);
      }
      if (filters?.search?.trim()) {
        const q = filters.search.trim();
        query = query.or(
          `title.ilike.%${q}%,category.ilike.%${q}%,grade.ilike.%${q}%,description.ilike.%${q}%`
        );
      }

      query = query.order('created_at', { ascending: false });

      const { data, error } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Worksheets fetch error:', error.message);
        return [];
      }
      return (data as WorksheetRow[]) || [];
    } catch (err: any) {
      console.log('Schema error:', err.message);
      throw err;
    }
  },

  async getPublished(filters?: {
    category?: CategoryName | 'All';
    grade?: GradeLevel | 'All';
    search?: string;
    sortBy?: 'newest' | 'most-downloaded' | 'title-az';
  }): Promise<WorksheetRow[]> {
    const supabase = createClient();
    try {
      let query = supabase.from('worksheets').select('*').eq('status', 'Published');

      if (filters?.category && filters.category !== 'All') {
        query = query.eq('category', filters.category);
      }
      if (filters?.grade && filters.grade !== 'All') {
        query = query.eq('grade', filters.grade);
      }
      if (filters?.search?.trim()) {
        const q = filters.search.trim();
        query = query.or(
          `title.ilike.%${q}%,category.ilike.%${q}%,grade.ilike.%${q}%,description.ilike.%${q}%`
        );
      }

      if (filters?.sortBy === 'most-downloaded') {
        query = query.order('downloads', { ascending: false });
      } else if (filters?.sortBy === 'title-az') {
        query = query.order('title', { ascending: true });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      const { data, error } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Worksheets fetch error:', error.message);
        return [];
      }
      return (data as WorksheetRow[]) || [];
    } catch (err: any) {
      console.log('Schema error:', err.message);
      throw err;
    }
  },

  async upload(input: WorksheetInsert): Promise<WorksheetRow | null> {
    const supabase = createClient();
    try {
      const ext = input.file.name.split('.').pop() || 'pdf';
      const safeName = input.title.toLowerCase().replace(/[^a-z0-9]/g, '-').slice(0, 50);
      const filePath = `${Date.now()}-${safeName}.${ext}`;

      const { error: storageError } = await supabase.storage
        .from('worksheets')
        .upload(filePath, input.file, { cacheControl: '3600', upsert: false });

      if (storageError) {
        console.log('Storage upload error:', storageError.message);
        throw new Error(storageError.message);
      }

      const fileSizeMB = (input.file.size / 1024 / 1024).toFixed(2) + ' MB';

      const { data, error } = await supabase
        .from('worksheets')
        .insert({
          title: input.title,
          category: input.category,
          grade: input.grade,
          description: input.description,
          file_path: filePath,
          file_name: input.file.name,
          file_size: fileSizeMB,
          status: 'Published',
          pages: 1,
          downloads: 0,
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Insert error:', error.message);
        return null;
      }
      return data as WorksheetRow;
    } catch (err: any) {
      console.log('Upload error:', err.message);
      throw err;
    }
  },

  async toggleStatus(id: string, currentStatus: WorksheetStatus): Promise<WorksheetRow | null> {
    const supabase = createClient();
    const nextStatus: WorksheetStatus = currentStatus === 'Published' ? 'Archived' : 'Published';
    try {
      const { data, error } = await supabase
        .from('worksheets')
        .update({ status: nextStatus })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Toggle status error:', error.message);
        return null;
      }
      return data as WorksheetRow;
    } catch (err: any) {
      console.log('Schema error:', err.message);
      throw err;
    }
  },

  async delete(id: string, filePath: string): Promise<boolean> {
    const supabase = createClient();
    try {
      // Delete file from storage
      await supabase.storage.from('worksheets').remove([filePath]);

      const { error } = await supabase.from('worksheets').delete().eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Delete error:', error.message);
        return false;
      }
      return true;
    } catch (err: any) {
      console.log('Schema error:', err.message);
      throw err;
    }
  },

  async incrementDownload(id: string): Promise<void> {
    const supabase = createClient();
    try {
      await supabase.rpc('increment_worksheet_downloads', { worksheet_id: id });
    } catch {
      // Non-critical, ignore
    }
  },

  getPublicUrl(filePath: string): string {
    const supabase = createClient();
    const { data } = supabase.storage.from('worksheets').getPublicUrl(filePath);
    return data.publicUrl;
  },
};
